import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { toast } from '@/stores/toastStore';
import { Button, Input, Card, CardContent } from '@/components/ui';

const loginSchema = z.object({
  email: z.string().email('有効なメールアドレスを入力してください'),
  password: z.string().min(1, 'パスワードを入力してください'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export function LoginPage() {
  const navigate = useNavigate();
  const { signIn, isLoading, clearError } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      setLoginError(null);
      clearError();
      await signIn(data.email, data.password);
      toast.success('ログインしました');
      navigate('/dashboard');
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'ログインに失敗しました。もう一度お試しください';
      setLoginError(message);
    }
  };

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-2xl font-heading-luxury text-luxury-dark mb-2">
          ログイン
        </h2>
        <p className="text-neutral-600">
          アカウントにログインしてください
        </p>
      </div>

      {/* Form */}
      <Card>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {loginError && (
              <div className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>{loginError}</span>
              </div>
            )}

            <Input
              {...register('email')}
              type="email"
              label="メールアドレス"
              placeholder="example@email.com"
              error={errors.email?.message}
              leftIcon={<Mail className="w-5 h-5" />}
              autoComplete="email"
            />

            <Input
              {...register('password')}
              type={showPassword ? 'text' : 'password'}
              label="パスワード"
              placeholder="パスワードを入力"
              error={errors.password?.message}
              leftIcon={<Lock className="w-5 h-5" />}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-neutral-400 hover:text-neutral-600"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              }
              autoComplete="current-password"
            />

            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-sm text-copper hover:underline"
              >
                パスワードをお忘れですか？
              </Link>
            </div>

            <Button type="submit" fullWidth isLoading={isLoading}>
              ログイン
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Register link */}
      <p className="text-center mt-6 text-neutral-600">
        アカウントをお持ちでない方は{' '}
        <Link to="/register" className="text-copper font-medium hover:underline">
          新規登録
        </Link>
      </p>
    </div>
  );
}
